import React from "react";
import { useDispatch } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import Snackbar from "@material-ui/core/Snackbar";
import Slide from "@material-ui/core/Slide";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import { CheckBoxOutlined, InfoOutlined, WarningOutlined } from "@material-ui/icons";
import { CLEAR_DESIGNER_MESSAGE } from "../redux_store/appState";

function SlideUpTransition(props) {
    return <Slide {...props} direction="up" />;
}

const useStyles = makeStyles({
    root: {
        background: "#313133",
        color: "white",
        padding: "8px",
        borderRadius: "5px",
        boxShadow: "5px 4px 10px #888888",
        font: '14px "Univers45", sans-serif',
        textAlign: "start",
        display: "flex",
        alignItems: "center",
    },
    success: {
        background: "#008200",
    },
    warning: {
        background: "#e79b36",
    },
    error: {
        background: "#EF4036",
    },
    info: {
        background: "#3667efff",
    },
    snackbar: {
        zIndex: 10100,
    },
});

function extractTextContent(node) {
    if (node == null) return "";

    if (typeof node === "string" || typeof node === "number") {
        return String(node);
    }
    if (Array.isArray(node)) {
        return node.map(extractTextContent).join("");
    }
    if (React.isValidElement(node)) {
        return extractTextContent(node.props.children);
    }
    if (typeof node === "object") {
        if (node.props && node.props.children !== undefined) {
            return extractTextContent(node.props.children);
        }
        if (node.children !== undefined) {
            return extractTextContent(node.children);
        }
        return "";
    }
    return "";
}

export default function Message(props) {
    const classes = useStyles();
    const dispatch = useDispatch();
    
    let messages = [];

    if (Array.isArray(props.message)) {
        messages = props.message;
    } else if (props.message) {
        messages = [props.message];
    }

    if (messages.length === 0) return null;

    const normalize = () => {
        return messages.map((msg, index) => {
            if (typeof msg === "string" || typeof msg === "number") {
                const text = String(msg);
                return {
                    id: text,
                    message: text,
                    type: "warning",
                };
            }

            if (React.isValidElement(msg) || Array.isArray(msg)) {
                const messageContent = msg;
                const text = extractTextContent(msg).trim();
                const id = text || String(index);
                return {
                    id,
                    message: messageContent,
                    type: "warning",
                };
            }

            let messageContent;
            if (typeof msg.message === "string" || typeof msg.message === "number") {
                messageContent = msg.message;
            } else if (React.isValidElement(msg.message) || Array.isArray(msg.message)) {
                messageContent = msg.message;
            } else if (msg.message && typeof msg.message === "object") {
                messageContent = msg.message;
            } else {
                messageContent = String(msg.message);
            }

            const rawType = msg.type ? String(msg.type).toLowerCase() : "warning";

            let id;
            if (msg.id != null) {
                id = String(msg.id);
            } else {
                // Extract text from messageContent for ID
                const text = extractTextContent(messageContent).trim();
                id = text || String(index);
            }

            const normalized = {
                id,
                message: messageContent,
                type: rawType,
                ...(msg.timeout !== undefined ? { timeout: msg.timeout } : {}),
                ...(msg.action ? { action: msg.action } : {}),
            };
            return normalized;
        });
    };

    const normalizedMessages = normalize();
    const baseOffset = 30;
    const perItemOffset = 70;

    return (
        <>
            {normalizedMessages.map((msg, index) => {
                const verticalOffset = baseOffset + index * perItemOffset;
                const typeClass = classes[msg.type] || classes.warning;

                let IconComponent = null;
                if (msg.type === "error") IconComponent = WarningOutlined;
                else if (msg.type === "warning") IconComponent = ErrorOutlineIcon;
                else if (msg.type === "success") IconComponent = CheckBoxOutlined;
                else if (msg.type === "info") IconComponent = InfoOutlined;

                return (
                    <Snackbar
                        key={msg.id}
                        open
                        autoHideDuration={msg?.timeout ?? 10000}
                        TransitionComponent={SlideUpTransition}
                        onClose={(event, reason) => {
                            if (reason === "clickaway") return;
                            dispatch({ type: CLEAR_DESIGNER_MESSAGE, payload: { message: msg.message } });
                        }}
                        style={{ bottom: verticalOffset }}
                        classes={{
                            root: classes.snackbar,
                        }}
                    >
                        <div className={`${classes.root} ${typeClass}`}>
                            {IconComponent && (
                                <IconComponent
                                    style={{
                                        width: 24,
                                        height: 24,
                                        marginRight: 8,
                                    }}
                                />
                            )}

                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                    flex: 1,
                                    gap: 12,
                                }}
                            >
                                {msg.message}
                                {msg.action && (
                                    <button
                                        onClick={() =>
                                            typeof msg.action.callback === "function" && msg.action.callback()
                                        }
                                        style={{
                                            background: "transparent",
                                            border: "1px solid white",
                                            color: "white",
                                            borderRadius: 4,
                                            padding: "4px 8px",
                                            cursor: "pointer",
                                            fontSize: 12,
                                        }}
                                    >
                                        {msg.action.label || "Action"}
                                    </button>
                                )}
                            </div>

                            <IconButton
                                size="small"
                                aria-label="close"
                                color="inherit"
                                onClick={() =>
                                    dispatch({ type: CLEAR_DESIGNER_MESSAGE, payload: { message: msg.message } })
                                }
                                style={{ marginLeft: 8 }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </div>
                    </Snackbar>
                );
            })}
        </>
    );
}
